import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';

function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState(null);

  const contactInfo = [
    {
      icon: <FaPhone />,
      title: "Phone",
      content: "(615) 651-1436",
      link: "tel:+6156511436"
    },
    {
      icon: <FaEnvelope />,
      title: "Email",
      content: "info@griffithmckee.com",
      link: "mailto:info@griffithmckee.com"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Office",
      content: "410 Seneca Ct, Nashville, TN 37214",
      link: "https://www.mapquest.com/us/tennessee/griffith-mckee-507642001"
    },
    {
      icon: <FaClock />,
      title: "Hours",
      content: "Mon-Fri: 8am-6pm",
      link: null
    }
  ];

  const projectTypes = [
    "Residential Renovation",
    "Commercial Construction",
    "New Home Build",
    "Outdoor Project",
    "Other"
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success');
      // Reset form after 2 seconds
      setTimeout(() => {
        setFormStatus(null);
        setFormState({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      }, 2000);
    }, 1000);
  };

  return (
    <div className="contact-container">
      {/* Hero Section */}
      <motion.section 
        className="contact-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="contact-hero-content">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Let's discuss your project and bring your vision to life
          </motion.p>
        </div>
      </motion.section>

      <div className="contact-content">
        {/* Contact Information */}
        <section className="contact-info-section">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Get in Touch
          </motion.h2>
          <div className="contact-info-grid">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="contact-info-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="info-icon">{info.icon}</div>
                <h3>{info.title}</h3>
                {info.link ? (
                  <a href={info.link} target={info.title === "Office" ? "_blank" : undefined} rel="noopener noreferrer">
                    {info.content}
                  </a>
                ) : (
                  <p>{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="contact-form-section">
          <motion.div
            className="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Send Us a Message</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your Name"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formState.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Your Email"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formState.phone}
                    onChange={handleInputChange}
                    placeholder="Your Phone Number"
                  />
                </motion.div>

                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <label htmlFor="projectType">Project Type</label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formState.projectType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Project Type</option>
                    {projectTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </motion.div>
              </div>

              <motion.div
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleInputChange}
                  required
                  placeholder="Tell us about your project"
                  rows="5"
                ></textarea>
              </motion.div>

              <motion.button
                type="submit"
                className={`submit-button ${formStatus ? formStatus : ''}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={formStatus === 'sending'}
              >
                {formStatus === 'sending' ? 'Sending...' :
                 formStatus === 'success' ? 'Message Sent!' :
                 'Send Message'}
              </motion.button>
            </form>
          </motion.div>
        </section>
      </div>
    </div>
  );
}

export default Contact;